import { ChevronLeft, ChevronRight } from "lucide-react";
import { LIST_LIMITS, type Page } from "@estelle/domain";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * A page size every pager may offer is one every route behind a pager will
 * serve: the API refuses a `limit` above its route's maximum. The smallest is
 * the sync routes' (100), so an option above any route's maximum drops out here
 * rather than becoming a 400 someone has to find.
 */
const SMALLEST_MAX = Math.min(
  ...Object.values(LIST_LIMITS).map((limits) => limits.max),
);
const DEFAULT_LIMIT_OPTIONS = [25, 50, 100].filter(
  (option) => option <= SMALLEST_MAX,
);

/**
 * The cursor trail, owned by the caller.
 *
 * `history[0]` is always `undefined` — the first page needs no cursor to
 * fetch. Every later entry is the cursor that was used to fetch the page at
 * that position, so `history[history.length - 1]` is always the cursor
 * behind the page currently on screen. Forward-only cursors have no way to
 * derive "the previous page" from "the current page" alone, so going back
 * means popping this stack rather than computing anything.
 *
 * This lives in props instead of component state so the coordinator can
 * back it with URL state later: a refresh should not strand someone back
 * on page 1 with no way to return to page 6 except clicking Next five
 * times again.
 *
 * A screen holds it with `useCursorHistory()`, which also returns to page one
 * when the filter, search or sort changes — the page size is the one reset a
 * screen does itself.
 */
export type CursorHistory = (string | undefined)[];

export interface PaginationProps {
  /** The page currently on screen. */
  page: Page<unknown>;
  /** Rows requested per page. */
  limit: number;
  onLimitChange: (limit: number) => void;
  /** Selectable rows-per-page values. */
  limitOptions?: number[];
  /** See `CursorHistory`. Always non-empty — seed it with `[undefined]`. */
  history: CursorHistory;
  /** Called with the updated history after Previous or Next. */
  onNavigate: (history: CursorHistory) => void;
}

/**
 * Previous / Next / rows-per-page / row-count, for cursor-paginated lists.
 *
 * No page numbers: an offset ("page 6 of 40") skips or repeats rows the
 * moment data shifts between requests, which is routine here — orders keep
 * arriving while someone pages through them. A cursor only ever says "give
 * me what comes after this," so that is all the control surface offers.
 */
export function Pagination({
  page,
  limit,
  onLimitChange,
  limitOptions = DEFAULT_LIMIT_OPTIONS,
  history,
  onNavigate,
}: PaginationProps) {
  const pageIndex = history.length - 1;
  const canGoBack = history.length > 1;
  const canGoForward = page.nextCursor !== null;

  // Cursors don't carry an offset, so the range shown is inferred from how
  // many full pages of `limit` rows came before — exact as long as `limit`
  // did not change mid-trail, which is the only case that matters here
  // since changing it resets the trail below.
  const start = pageIndex * limit + 1;
  const end = start + page.items.length - 1;

  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <div className="text-muted-foreground flex min-w-0 items-center gap-1.5">
        <span className="hidden sm:inline">Rows per page</span>
        <Select
          value={String(limit)}
          onValueChange={(value) => {
            onLimitChange(Number(value));
            // The trail is positions within a page size; changing the size
            // invalidates every offset in it, so start over from page one
            // rather than leave `history` pointing at rows that no longer
            // line up.
            onNavigate([undefined]);
          }}
        >
          <SelectTrigger size="sm" className="w-[4.5rem]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {limitOptions.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="text-muted-foreground truncate tabular-nums">
        {page.items.length === 0 ? (
          "No rows"
        ) : page.total === null ? (
          // No total means no way to say how many rows are left, so this
          // names the page instead of a range that would imply an end.
          <span>Page {pageIndex + 1}</span>
        ) : (
          <span>
            {start}–{end} of {page.total}
          </span>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label="Previous page"
          disabled={!canGoBack}
          onClick={() => onNavigate(history.slice(0, -1))}
        >
          <ChevronLeft />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label="Next page"
          disabled={!canGoForward}
          onClick={() => {
            if (page.nextCursor !== null) {
              onNavigate([...history, page.nextCursor]);
            }
          }}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}
